import { useCallback, useEffect, useRef, useState } from "react";
import { useWebSocketContext } from "../contexts/WebSocketProvider";

export default function useFirmwareUpdate() {
  const { send, isConnected, isOpen, connect } = useWebSocketContext();

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const mountedRef = useRef(true);
  const connectionAttemptedRef = useRef(false);

  const firmwareUpdate = useCallback(
    async ({ stationId }) => {
      if (!mountedRef.current) return;

      setIsLoading(true);
      setError("");

      try {
        // Ensure WebSocket connection
        if (!isConnected) {
          if (!connectionAttemptedRef.current) {
            connectionAttemptedRef.current = true;
            await connect();
          }

          await new Promise((resolve, reject) => {
            const start = Date.now();
            const interval = setInterval(() => {
              if (!mountedRef.current) {
                clearInterval(interval);
                reject(new Error("Component unmounted"));
              } else if (isOpen()) {
                clearInterval(interval);
                resolve(true);
              } else if (Date.now() - start > 10000) {
                clearInterval(interval);
                reject(new Error("WebSocket connection timeout"));
              }
            }, 100);
          });
        }

        // Prepare request (same as Flutter code)
        const request = {
          type: "cms_firmware_update",
          station_id: stationId
        };

        console.log("Sending firmware update:", request);

        const response = await send("cms_firmware_update", request);
        console.log("Firmware Update Response:", response);

        return { success: true, response };
      } catch (err) {
        setError(err.message);
        return { success: false, error: err.message };
      } finally {
        if (mountedRef.current) setIsLoading(false);
      }
    },
    [isConnected, isOpen, connect, send]
  );

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  return {
    firmwareUpdate,
    isLoading,
    error,
  };
}
